import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, CardLoadingSkeleton, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
    Plus, Trash, PencilSimple, ClockCounterClockwise, Users as UsersIcon,
    CalendarBlank, X, Info, ArrowSquareOut, ShieldCheck, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const toISODate = (d) => format(d, "yyyy-MM-dd");

const AMENITY_IMAGES = {
    "clubhouse": "https://images.unsplash.com/photo-1519974719765-e6559eac2575?crop=entropy&cs=tinysrgb&fm=jpg&w=800&q=80",
    "gym": "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=tinysrgb&fm=jpg&w=800&q=80",
    "hall": "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?crop=entropy&cs=tinysrgb&fm=jpg&w=800&q=80",
    "pool": "https://images.unsplash.com/photo-1519315901367-f34ff9154487?crop=entropy&cs=tinysrgb&fm=jpg&w=800&q=80",
    "garden": "https://images.unsplash.com/photo-1560493676-04071c5f467b?crop=entropy&cs=tinysrgb&fm=jpg&w=800&q=80",
    "default": "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=tinysrgb&fm=jpg&w=800&q=80",
};

const REFUND_POLICY_LABELS = {
    full: "Full refund",
    partial: "Partial refund",
    none: "No refund",
    no_refund_after_deadline: "No refund after deadline",
};

const DEFAULT_POLICY = { allow_cancellation: true, deadline_hours: 24, refund_policy: "no_refund_after_deadline", refund_percentage: null };

function pickImage(name = "", url) {
    if (url) return url;
    const key = Object.keys(AMENITY_IMAGES).find(k => name.toLowerCase().includes(k));
    return AMENITY_IMAGES[key || "default"];
}

function policyRuleLines(policy, priced) {
    const p = policy || DEFAULT_POLICY;
    const lines = [];
    if (!p.allow_cancellation) {
        lines.push("Cancellation is not permitted for this amenity — contact the committee.");
        return lines;
    }
    if (!priced) {
        lines.push("Free amenity — cancel anytime before your slot, no refund applicable.");
        return lines;
    }
    if (p.refund_policy === "full") lines.push("Cancel anytime before your slot for a full refund.");
    else if (p.refund_policy === "none") lines.push("Bookings are non-refundable once confirmed.");
    else if (p.refund_policy === "partial") lines.push(`Cancel anytime before your slot for a ${p.refund_percentage ?? 50}% refund.`);
    else lines.push(`Cancellation allowed up to ${p.deadline_hours}h before the slot for a full refund. Inside that window, no refund.`);
    return lines;
}

function paymentStatusVariant(status) {
    if (status === "PAID") return "success";
    if (status === "PARTIAL") return "warn";
    if (status === "FREE") return "default";
    return "danger";
}

function refundStatusLabel(status) {
    if (status === "REFUNDED") return "Refunded";
    if (status === "REFUND_DUE") return "Not processed";
    return "Not applicable";
}

export default function Amenities() {
    const { user } = useAuth();
    const isStaff = user?.role === "admin" || user?.role === "committee";
    const [amenities, setAmenities] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [tab, setTab] = useState("browse");
    const [loading, setLoading] = useState(true);

    const [amenityOpen, setAmenityOpen] = useState(false);
    const [amEditing, setAmEditing] = useState(null);
    const [amForm, setAmForm] = useState({
        name: "", description: "", capacity: "", open_time: "06:00", close_time: "22:00",
        slot_duration_minutes: 60, price_per_slot: 0, is_active: true, image_url: "",
        cancellation_policy: { ...DEFAULT_POLICY },
    });

    const [bookOpen, setBookOpen] = useState(false);
    const [bookAmenity, setBookAmenity] = useState(null);
    const [bookDate, setBookDate] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState([new Date()]);
    const [slots, setSlots] = useState([]);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [bookNotes, setBookNotes] = useState("");
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [showSummary, setShowSummary] = useState(false);

    // Booking detail dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailBooking, setDetailBooking] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [refundNote, setRefundNote] = useState("");
    const [processingRefund, setProcessingRefund] = useState(false);

    // Cancellation confirm dialog
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelPreview, setCancelPreview] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);

    async function loadAll() {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([api.get("/amenities"), api.get("/bookings")]);
            setAmenities(a.data); setBookings(b.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { loadAll(); }, []);

    // ---- Amenity CRUD (staff) ----
    function openCreateAmenity() {
        setAmEditing(null);
        setAmForm({
            name: "", description: "", capacity: "", open_time: "06:00", close_time: "22:00",
            slot_duration_minutes: 60, price_per_slot: 0, is_active: true, image_url: "",
            cancellation_policy: { ...DEFAULT_POLICY },
        });
        setAmenityOpen(true);
    }
    function openEditAmenity(a) {
        setAmEditing(a);
        setAmForm({
            name: a.name, description: a.description || "", capacity: a.capacity || "",
            open_time: a.open_time, close_time: a.close_time,
            slot_duration_minutes: a.slot_duration_minutes, price_per_slot: a.price_per_slot,
            is_active: !!a.is_active, image_url: a.image_url || "",
            cancellation_policy: { ...DEFAULT_POLICY, ...(a.cancellation_policy || {}) },
        });
        setAmenityOpen(true);
    }
    async function submitAmenity(e) {
        e.preventDefault();
        const cp = amForm.cancellation_policy;
        const payload = {
            ...amForm,
            capacity: amForm.capacity ? Number(amForm.capacity) : null,
            slot_duration_minutes: Number(amForm.slot_duration_minutes) || 60,
            price_per_slot: Number(amForm.price_per_slot) || 0,
            cancellation_policy: {
                allow_cancellation: !!cp.allow_cancellation,
                deadline_hours: Number(cp.deadline_hours) || 0,
                refund_policy: cp.refund_policy,
                refund_percentage: cp.refund_policy === "partial" ? Number(cp.refund_percentage) || 0 : null,
            },
        };
        try {
            if (amEditing) {
                await api.patch(`/amenities/${amEditing.id}`, payload);
                toast.success("Amenity updated");
            } else {
                await api.post("/amenities", payload);
                toast.success("Amenity added");
            }
            setAmenityOpen(false);
            loadAll();
        } catch (err) { toast.error(formatError(err)); }
    }
    async function removeAmenity(a) {
        if (!confirm(`Delete ${a.name}? Future bookings will be cancelled.`)) return;
        try { await api.delete(`/amenities/${a.id}`); toast.success("Deleted"); loadAll(); }
        catch (e) { toast.error(formatError(e)); }
    }

    // ---- Booking flow ----
    async function openBook(a) {
        setBookAmenity(a);
        const today = new Date();
        setBookDate(today);
        setSelectedDates([today]);
        setSelectedSlots([]);
        setBookNotes("");
        setShowSummary(false);
        setBookOpen(true);
        await fetchSlots(a.id, today);
    }

    async function fetchSlots(amenityId, date) {
        setLoadingSlots(true);
        try {
            const { data } = await api.get(`/amenities/${amenityId}/slots`, { params: { date: toISODate(date) } });
            setSlots(data.slots);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoadingSlots(false); }
    }

    useEffect(() => {
        if (bookAmenity && bookOpen) fetchSlots(bookAmenity.id, bookDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookDate]);

    function toggleSlotSelection(slot) {
        const dateStr = toISODate(bookDate);
        const exists = selectedSlots.some(s => s.date === dateStr && s.start_time === slot.start_time);
        if (exists) {
            setSelectedSlots(selectedSlots.filter(s => !(s.date === dateStr && s.start_time === slot.start_time)));
        } else {
            setSelectedSlots([...selectedSlots, { ...slot, date: dateStr }]);
        }
    }

    function getTotalPrice() {
        return selectedSlots.length * (bookAmenity?.price_per_slot || 0);
    }

    async function submitBooking() {
        if (selectedSlots.length === 0) {
            toast.error("Please select at least one time slot");
            return;
        }

        if (!showSummary) {
            setShowSummary(true);
            return;
        }

        try {
            const promises = selectedSlots.map(slot =>
                api.post("/bookings", {
                    amenity_id: bookAmenity.id,
                    date: slot.date,
                    start_time: slot.start_time,
                    end_time: slot.end_time,
                    notes: bookNotes || null,
                })
            );

            await Promise.all(promises);
            toast.success(`${selectedSlots.length} booking(s) confirmed! Check your email.`);
            setBookOpen(false);
            setShowSummary(false);
            loadAll();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    // ---- Booking detail ----
    async function openDetail(b) {
        setDetailOpen(true);
        setDetailLoading(true);
        setRefundNote("");
        try {
            const { data } = await api.get(`/bookings/${b.id}`);
            setDetailBooking(data);
        } catch (e) { toast.error(formatError(e)); setDetailOpen(false); }
        finally { setDetailLoading(false); }
    }

    async function markRefundProcessed() {
        if (!detailBooking) return;
        setProcessingRefund(true);
        try {
            const { data } = await api.post(`/bookings/${detailBooking.id}/refund/mark-processed`, { note: refundNote || null });
            setDetailBooking(data);
            toast.success("Refund marked as processed");
            loadAll();
        } catch (e) { toast.error(formatError(e)); }
        finally { setProcessingRefund(false); }
    }

    // ---- Cancellation flow ----
    async function openCancel(b) {
        setCancelTarget(b);
        setCancelReason("");
        setCancelOpen(true);
        setCancelLoading(true);
        setCancelPreview(null);
        try {
            const { data } = await api.get(`/bookings/${b.id}/cancellation-preview`);
            setCancelPreview(data);
        } catch (e) { toast.error(formatError(e)); setCancelOpen(false); }
        finally { setCancelLoading(false); }
    }

    async function confirmCancel() {
        if (!cancelTarget) return;
        setCancelSubmitting(true);
        try {
            await api.post(`/bookings/${cancelTarget.id}/cancel`, { reason: cancelReason || null });
            toast.success("Booking cancelled");
            setCancelOpen(false);
            if (detailOpen && detailBooking?.id === cancelTarget.id) setDetailOpen(false);
            loadAll();
        } catch (e) { toast.error(formatError(e)); }
        finally { setCancelSubmitting(false); }
    }

    const upcomingBookings = useMemo(() => {
        const today = toISODate(new Date());
        return bookings.filter(b => b.date >= today && b.status !== "cancelled")
            .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
    }, [bookings]);

    const pastBookings = useMemo(() => {
        const today = toISODate(new Date());
        return bookings.filter(b => b.date < today || b.status === "cancelled");
    }, [bookings]);

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Community perks"
                title="Amenities"
                description="Book the clubhouse, gym, hall and more. Slot conflicts are auto-prevented."
                actions={isStaff && (
                    <Button data-testid="add-amenity-btn" onClick={openCreateAmenity}
                        className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                        <Plus size={16} className="mr-1.5" /> Add amenity
                    </Button>
                )}
            />

            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="bg-white border border-brand-line rounded-full p-1 mb-6">
                    <TabsTrigger data-testid="tab-browse" value="browse" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">Browse</TabsTrigger>
                    <TabsTrigger data-testid="tab-mybookings" value="mine" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                        My bookings {upcomingBookings.length > 0 && <span className="ml-1.5 text-[10px] bg-brand-action text-white rounded-full px-1.5">{upcomingBookings.length}</span>}
                    </TabsTrigger>
                    {isStaff && <TabsTrigger data-testid="tab-all-bookings" value="all" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">All bookings</TabsTrigger>}
                </TabsList>

                <TabsContent value="browse">
                    {loading ? (
                        <CardLoadingSkeleton count={6} />
                    ) : amenities.length === 0 ? (
                        <EmptyState title="No amenities yet" description={isStaff ? "Add your first amenity — clubhouse, gym, party hall..." : "Committee will add amenities soon."} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {amenities.map((a) => (
                                <div key={a.id} data-testid={`amenity-card-${a.id}`} className="bg-white border border-brand-line rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 flex flex-col">
                                    <div className="h-40 bg-brand-sage overflow-hidden relative">
                                        <img src={pickImage(a.name, a.image_url)} alt={a.name} className="w-full h-full object-cover" />
                                        {!a.is_active && (
                                            <div className="absolute inset-0 bg-brand-ink/60 flex items-center justify-center">
                                                <Chip variant="danger">Inactive</Chip>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <h3 className="font-heading text-xl text-brand-ink tracking-tight">{a.name}</h3>
                                                {a.description && <p className="text-sm text-brand-inkSoft mt-1 leading-relaxed line-clamp-2">{a.description}</p>}
                                            </div>
                                            {a.price_per_slot > 0 && (
                                                <div className="text-right">
                                                    <p className="font-heading text-brand-ink text-lg">{inr(a.price_per_slot)}</p>
                                                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">per slot</p>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4 mt-4 text-xs text-brand-inkSoft">
                                            <span className="inline-flex items-center gap-1"><ClockCounterClockwise size={14} /> {a.open_time}–{a.close_time}</span>
                                            <span className="inline-flex items-center gap-1"><CalendarBlank size={14} /> {a.slot_duration_minutes} min</span>
                                            {a.capacity && <span className="inline-flex items-center gap-1"><UsersIcon size={14} /> {a.capacity}</span>}
                                        </div>
                                        <div data-testid={`amenity-rules-${a.id}`} className="mt-3 pt-3 border-t border-dashed border-brand-line/70 flex items-start gap-1.5 text-[11px] text-brand-inkSoft leading-snug">
                                            <Info size={13} className="mt-0.5 shrink-0" />
                                            <span>{policyRuleLines(a.cancellation_policy, a.price_per_slot > 0)[0]}</span>
                                        </div>
                                        <div className="mt-4 flex items-center gap-2">
                                            <Button data-testid={`book-amenity-${a.id}`} onClick={() => openBook(a)} disabled={!a.is_active}
                                                className="flex-1 rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                                                Book
                                            </Button>
                                            {isStaff && (
                                                <>
                                                    <button data-testid={`edit-amenity-${a.id}`} onClick={() => openEditAmenity(a)} className="p-2 hover:bg-brand-sage rounded-sm"><PencilSimple size={14} /></button>
                                                    {user?.role === "admin" && (
                                                        <button data-testid={`delete-amenity-${a.id}`} onClick={() => removeAmenity(a)} className="p-2 hover:bg-[#FEF2F2] rounded-sm text-brand-action"><Trash size={14} /></button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="mine">
                    <BookingsTable bookings={[...upcomingBookings, ...pastBookings.filter(b => b.user_id === user.id)]} onCancel={openCancel} onOpen={openDetail} user={user} showUser={false} />
                </TabsContent>

                {isStaff && (
                    <TabsContent value="all">
                        <BookingsTable bookings={bookings} onCancel={openCancel} onOpen={openDetail} user={user} showUser={true} />
                    </TabsContent>
                )}
            </Tabs>

            {/* Amenity create/edit dialog */}
            <Dialog open={amenityOpen} onOpenChange={setAmenityOpen}>
                <DialogContent className="rounded-sm max-w-lg max-h-[88vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{amEditing ? "Edit amenity" : "Add amenity"}</DialogTitle></DialogHeader>
                    <form onSubmit={submitAmenity} className="space-y-4">
                        <div className="space-y-2"><Label>Name</Label>
                            <Input data-testid="amenity-name-input" required value={amForm.name} onChange={(e) => setAmForm({ ...amForm, name: e.target.value })} placeholder="Clubhouse" className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2"><Label>Description</Label>
                            <Textarea rows={2} value={amForm.description} onChange={(e) => setAmForm({ ...amForm, description: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Open time</Label>
                                <Input type="time" required value={amForm.open_time} onChange={(e) => setAmForm({ ...amForm, open_time: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2"><Label>Close time</Label>
                                <Input type="time" required value={amForm.close_time} onChange={(e) => setAmForm({ ...amForm, close_time: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2"><Label>Slot (min)</Label>
                                <Input type="number" min={15} step={15} value={amForm.slot_duration_minutes} onChange={(e) => setAmForm({ ...amForm, slot_duration_minutes: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2"><Label>Price / slot (₹)</Label>
                                <Input type="number" min={0} step={50} value={amForm.price_per_slot} onChange={(e) => setAmForm({ ...amForm, price_per_slot: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2"><Label>Capacity</Label>
                                <Input type="number" min={0} value={amForm.capacity} onChange={(e) => setAmForm({ ...amForm, capacity: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="space-y-2"><Label>Image URL (optional)</Label>
                            <Input value={amForm.image_url} onChange={(e) => setAmForm({ ...amForm, image_url: e.target.value })} placeholder="https://..." className="rounded-sm border-brand-line" />
                        </div>
                        <div className="flex items-center justify-between border border-brand-line rounded-sm p-3">
                            <div>
                                <Label>Active</Label>
                                <p className="text-xs text-brand-inkSoft">Residents can only book active amenities</p>
                            </div>
                            <Switch data-testid="amenity-active-switch" checked={amForm.is_active} onCheckedChange={(v) => setAmForm({ ...amForm, is_active: v })} />
                        </div>

                        {/* Cancellation Policy */}
                        <div className="border border-brand-line rounded-sm p-4 space-y-3 bg-brand-bg/40">
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-medium">Cancellation Policy</p>
                            <div className="flex items-center justify-between">
                                <Label>Allow cancellation</Label>
                                <Switch data-testid="amenity-cancellation-allow-switch"
                                    checked={amForm.cancellation_policy.allow_cancellation}
                                    onCheckedChange={(v) => setAmForm({ ...amForm, cancellation_policy: { ...amForm.cancellation_policy, allow_cancellation: v } })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Cancellation deadline (hours before)</Label>
                                    <Input data-testid="amenity-cancellation-deadline-input" type="number" min={0} max={720}
                                        value={amForm.cancellation_policy.deadline_hours}
                                        onChange={(e) => setAmForm({ ...amForm, cancellation_policy: { ...amForm.cancellation_policy, deadline_hours: e.target.value } })}
                                        className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Refund policy</Label>
                                    <select data-testid="amenity-refund-policy-select"
                                        value={amForm.cancellation_policy.refund_policy}
                                        onChange={(e) => setAmForm({ ...amForm, cancellation_policy: { ...amForm.cancellation_policy, refund_policy: e.target.value } })}
                                        className="w-full h-9 rounded-sm border border-brand-line bg-white text-sm px-3">
                                        {Object.entries(REFUND_POLICY_LABELS).map(([k, label]) => (
                                            <option key={k} value={k}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {amForm.cancellation_policy.refund_policy === "partial" && (
                                <div className="space-y-2">
                                    <Label>Refund percentage (%)</Label>
                                    <Input data-testid="amenity-refund-percentage-input" type="number" min={0} max={100}
                                        value={amForm.cancellation_policy.refund_percentage ?? 50}
                                        onChange={(e) => setAmForm({ ...amForm, cancellation_policy: { ...amForm.cancellation_policy, refund_percentage: e.target.value } })}
                                        className="rounded-sm border-brand-line" />
                                </div>
                            )}
                            <p className="text-xs text-brand-inkSoft leading-snug">{policyRuleLines(amForm.cancellation_policy, Number(amForm.price_per_slot) > 0)[0]}</p>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAmenityOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="amenity-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">{amEditing ? "Save" : "Add"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Booking dialog */}
            <Dialog open={bookOpen} onOpenChange={setBookOpen}>
                <DialogContent className="rounded-sm max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">Book {bookAmenity?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-2">Pick a date</p>
                            <div className="border border-brand-line rounded-sm p-2 bg-white">
                                <Calendar
                                    mode="single"
                                    selected={bookDate}
                                    onSelect={(d) => d && setBookDate(d)}
                                    disabled={(d) => d < new Date(new Date().toDateString())}
                                    className="rounded-sm"
                                    data-testid="booking-calendar"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-2">
                                Available slots · {format(bookDate, "EEE, MMM d")}
                            </p>
                            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                                {loadingSlots && <p className="col-span-2 text-sm text-brand-inkSoft py-6 text-center">Loading slots...</p>}
                                {!loadingSlots && slots.length === 0 && <p className="col-span-2 text-sm text-brand-inkSoft py-6 text-center">No slots for this day.</p>}
                                {slots.map((s) => {
                                    const dateStr = toISODate(bookDate);
                                    const active = selectedSlots.some(slot => slot.date === dateStr && slot.start_time === s.start_time);
                                    return (
                                        <button
                                            key={s.start_time}
                                            data-testid={`slot-${s.start_time}`}
                                            disabled={s.booked}
                                            onClick={() => toggleSlotSelection(s)}
                                            className={`text-left px-3 py-2 rounded-sm text-sm border transition-colors duration-150 ${
                                                s.booked
                                                    ? "bg-brand-bg border-brand-line text-brand-inkSoft cursor-not-allowed line-through"
                                                    : active
                                                    ? "bg-brand-action text-white border-brand-action"
                                                    : "bg-white border-brand-line text-brand-ink hover:border-brand-action"
                                            }`}
                                        >
                                            <p className="font-medium">{s.start_time} – {s.end_time}</p>
                                            <p className="text-[10px] uppercase tracking-overline mt-0.5 opacity-70">{s.booked ? "Booked" : active ? "Selected" : "Available"}</p>
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedSlots.length > 0 && (
                                <div className="mt-3 p-3 bg-brand-bg rounded-sm border border-brand-line">
                                    <p className="text-xs font-medium text-brand-ink mb-1">Selected: {selectedSlots.length} slot(s)</p>
                                    {bookAmenity?.price_per_slot > 0 && (
                                        <p className="text-sm text-brand-inkSoft">
                                            Total: <span className="font-heading text-brand-ink">{inr(getTotalPrice())}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                            {bookAmenity && (
                                <div className="mt-3 flex items-start gap-1.5 text-[11px] text-brand-inkSoft leading-snug">
                                    <ShieldCheck size={13} className="mt-0.5 shrink-0" />
                                    <span>{policyRuleLines(bookAmenity.cancellation_policy, bookAmenity.price_per_slot > 0)[0]}</span>
                                </div>
                            )}
                            <div className="space-y-2 mt-4">
                                <Label>Notes (optional)</Label>
                                <Textarea rows={2} value={bookNotes} onChange={(e) => setBookNotes(e.target.value)} placeholder="Birthday party, guests count..." className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                    </div>

                    {/* Booking Summary */}
                    {showSummary && selectedSlots.length > 0 && (
                        <div className="mt-4 p-4 bg-brand-sage/30 border border-brand-action rounded-sm">
                            <p className="font-heading text-brand-ink mb-3 text-lg">Booking Summary</p>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-brand-inkSoft">Amenity:</span>
                                    <span className="font-medium text-brand-ink">{bookAmenity?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-brand-inkSoft">Total Slots:</span>
                                    <span className="font-medium text-brand-ink">{selectedSlots.length}</span>
                                </div>
                                <div className="border-t border-brand-line pt-2 mt-2">
                                    {selectedSlots.map((slot, idx) => (
                                        <div key={idx} className="flex justify-between text-xs py-1">
                                            <span>{format(new Date(slot.date), "EEE, MMM d")}</span>
                                            <span className="font-medium">{slot.start_time} - {slot.end_time}</span>
                                        </div>
                                    ))}
                                </div>
                                {bookAmenity?.price_per_slot > 0 && (
                                    <div className="flex justify-between border-t border-brand-line pt-2 font-heading text-base">
                                        <span className="text-brand-ink">Total Amount:</span>
                                        <span className="text-brand-action">{inr(getTotalPrice())}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        {showSummary && (
                            <Button type="button" variant="outline" onClick={() => setShowSummary(false)} className="rounded-full">
                                Back
                            </Button>
                        )}
                        <Button type="button" variant="outline" onClick={() => { setBookOpen(false); setShowSummary(false); }} className="rounded-full">Cancel</Button>
                        <Button type="button" onClick={submitBooking} data-testid="booking-submit-btn"
                            disabled={selectedSlots.length === 0}
                            className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {showSummary ? "Confirm Booking" : "Continue"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Booking detail dialog */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent data-testid="booking-detail-dialog" className="rounded-sm max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Booking details</DialogTitle></DialogHeader>
                    {detailLoading || !detailBooking ? (
                        <p className="text-sm text-brand-inkSoft py-6 text-center">Loading…</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="border border-brand-line rounded-sm p-4 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Booking</p>
                                <p className="font-heading text-lg text-brand-ink">{detailBooking.amenity_name}</p>
                                <p className="text-sm text-brand-inkSoft">{detailBooking.booking_number}</p>
                                <p className="text-sm text-brand-ink">{format(new Date(detailBooking.date), "d MMM yyyy")} · {detailBooking.start_time} – {detailBooking.end_time}</p>
                                {user.role !== "resident" && (
                                    <p className="text-sm text-brand-inkSoft">{detailBooking.flat_label || "—"} · {detailBooking.user_name}</p>
                                )}
                                <div className="pt-1"><Chip variant={detailBooking.status === "cancelled" ? "danger" : "success"}>{detailBooking.status}</Chip></div>
                            </div>

                            <div className="border border-brand-line rounded-sm p-4 space-y-2" data-testid="booking-detail-payment">
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Payment</p>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-brand-inkSoft">Booking fee</span>
                                    <span className="font-heading text-brand-ink">{inr(detailBooking.price)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-brand-inkSoft">Status</span>
                                    <Chip data-testid="booking-detail-payment-status" variant={paymentStatusVariant(detailBooking.payment_status)}>{detailBooking.payment_status}</Chip>
                                </div>
                                {detailBooking.invoice && (
                                    <div className="flex items-center justify-between pt-1">
                                        <span className="text-sm text-brand-inkSoft">Invoice</span>
                                        <Link data-testid={`view-invoice-link-${detailBooking.id}`} to={`/app/invoices/${detailBooking.invoice.id}`}
                                            className="text-brand-action text-sm font-medium inline-flex items-center gap-1 hover:underline">
                                            {detailBooking.invoice.invoice_ref} <ArrowSquareOut size={12} />
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {detailBooking.status === "cancelled" && (
                                <div className="border border-brand-line rounded-sm p-4 space-y-2" data-testid="booking-detail-cancellation">
                                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Cancellation</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-brand-inkSoft">Cancelled at</span>
                                        <span className="text-sm text-brand-ink">{detailBooking.cancelled_at ? format(new Date(detailBooking.cancelled_at), "d MMM yyyy, HH:mm") : "—"}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-brand-inkSoft">Cancelled by</span>
                                        <span className="text-sm text-brand-ink capitalize">{detailBooking.cancelled_by_role || "—"}</span>
                                    </div>
                                    {detailBooking.cancellation_reason && (
                                        <div className="flex items-start justify-between gap-3">
                                            <span className="text-sm text-brand-inkSoft shrink-0">Reason</span>
                                            <span className="text-sm text-brand-ink text-right">{detailBooking.cancellation_reason}</span>
                                        </div>
                                    )}
                                    {detailBooking.price > 0 && (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-brand-inkSoft">Refund eligibility</span>
                                                <span className="font-medium text-brand-ink">{inr(detailBooking.refund_eligible_amount)} ({detailBooking.refund_eligible_pct ?? 0}%)</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-brand-inkSoft">Refund status</span>
                                                <Chip data-testid="booking-detail-refund-status" variant={detailBooking.refund_status === "REFUNDED" ? "success" : detailBooking.refund_status === "REFUND_DUE" ? "warn" : "default"}>
                                                    {refundStatusLabel(detailBooking.refund_status)}
                                                </Chip>
                                            </div>
                                            {detailBooking.refund_processed_note && (
                                                <p className="text-xs text-brand-inkSoft italic">Note: {detailBooking.refund_processed_note}</p>
                                            )}
                                            {isStaff && detailBooking.refund_status === "REFUND_DUE" && (
                                                <div className="pt-2 space-y-2 border-t border-dashed border-brand-line">
                                                    <Label className="text-xs">Mark refund as processed (bookkeeping only — no gateway integration)</Label>
                                                    <Textarea data-testid="refund-process-note-input" rows={2} value={refundNote} onChange={(e) => setRefundNote(e.target.value)}
                                                        placeholder="e.g. Refunded ₹500 in cash on 12 Feb" className="rounded-sm border-brand-line text-xs" />
                                                    <Button data-testid="mark-refund-processed-btn" onClick={markRefundProcessed} disabled={processingRefund}
                                                        className="w-full rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white text-sm">
                                                        <CheckCircle size={14} className="mr-1.5" /> {processingRefund ? "Processing…" : "Mark refund as processed"}
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {detailBooking.status !== "cancelled" && detailBooking.date >= toISODate(new Date()) &&
                                (detailBooking.user_id === user.id || isStaff) && (
                                <Button data-testid={`detail-cancel-booking-${detailBooking.id}`} variant="outline"
                                    onClick={() => { setDetailOpen(false); openCancel(detailBooking); }}
                                    className="w-full rounded-full border-brand-action text-brand-action hover:bg-[#FEF2F2]">
                                    <X size={14} className="mr-1.5" /> Cancel booking
                                </Button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Cancellation confirm dialog */}
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent data-testid="cancel-confirm-dialog" className="rounded-sm max-w-md">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Cancel booking?</DialogTitle></DialogHeader>
                    {cancelLoading || !cancelPreview ? (
                        <p className="text-sm text-brand-inkSoft py-6 text-center">Checking refund eligibility…</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="border border-brand-line rounded-sm p-4 space-y-1.5">
                                <p className="font-medium text-brand-ink">{cancelTarget?.amenity_name}</p>
                                <p className="text-sm text-brand-inkSoft">{cancelTarget && format(new Date(cancelTarget.date), "d MMM yyyy")} · {cancelTarget?.start_time} – {cancelTarget?.end_time}</p>
                                {cancelPreview.booking_fee > 0 && <p className="text-sm text-brand-inkSoft">Booking fee: <span className="font-medium text-brand-ink">{inr(cancelPreview.booking_fee)}</span></p>}
                            </div>
                            {!cancelPreview.can_cancel ? (
                                <p data-testid="cancel-not-allowed-msg" className="text-sm text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-md p-3">{cancelPreview.policy_message}</p>
                            ) : (
                                <div className="bg-brand-bg rounded-sm p-3 space-y-2">
                                    <p className="text-sm text-brand-inkSoft">{cancelPreview.policy_message}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-brand-ink">Refund</span>
                                        <span data-testid="cancel-refund-amount" className="font-heading text-lg text-brand-action">{inr(cancelPreview.refund_eligible_amount)}</span>
                                    </div>
                                </div>
                            )}
                            {isStaff && cancelPreview.can_cancel && (
                                <div className="space-y-2">
                                    <Label className="text-xs">Cancellation reason (optional)</Label>
                                    <Textarea data-testid="cancel-reason-input" rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                                        placeholder="e.g. Amenity unavailable due to maintenance" className="rounded-sm border-brand-line text-sm" />
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button data-testid="cancel-keep-booking-btn" type="button" variant="outline" onClick={() => setCancelOpen(false)} className="rounded-full">Keep booking</Button>
                        {cancelPreview?.can_cancel && (
                            <Button data-testid="cancel-confirm-btn" type="button" onClick={confirmCancel} disabled={cancelSubmitting}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {cancelSubmitting ? "Cancelling…" : "Cancel booking"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function BookingsTable({ bookings, onCancel, onOpen, user, showUser }) {
    if (bookings.length === 0) return <EmptyState title="No bookings" description="Nothing booked yet." />;
    const today = format(new Date(), "yyyy-MM-dd");
    return (
        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-brand-bg border-b border-brand-line text-left">
                        {["Amenity", "Date", "Time", ...(showUser ? ["Booked by"] : []), "Status", "Payment", ""].map(h => (
                            <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {bookings.map((b) => {
                        const isFuture = b.date >= today && b.status !== "cancelled";
                        const canCancel = isFuture && (b.user_id === user.id || user.role !== "resident");
                        return (
                            <tr key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => onOpen(b)} className="border-b border-brand-line/60 hover:bg-brand-bg/50 cursor-pointer">
                                <td className="py-3 px-4 font-medium text-brand-ink">{b.amenity_name || b.amenity?.name || "—"}</td>
                                <td className="py-3 px-4 text-brand-inkSoft">{b.date}</td>
                                <td className="py-3 px-4 text-brand-inkSoft">{b.start_time} – {b.end_time}</td>
                                {showUser && <td className="py-3 px-4 text-brand-inkSoft">{b.user_name} {b.flat_label && `(${b.flat_label})`}</td>}
                                <td className="py-3 px-4">
                                    <Chip variant={b.status === "cancelled" ? "danger" : b.status === "confirmed" ? "success" : "default"}>{b.status}</Chip>
                                </td>
                                <td className="py-3 px-4">
                                    <Chip data-testid={`booking-payment-status-${b.id}`} variant={paymentStatusVariant(b.payment_status)}>{b.payment_status || "—"}</Chip>
                                </td>
                                <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                                    {canCancel && (
                                        <button data-testid={`cancel-booking-${b.id}`} onClick={() => onCancel(b)} className="text-brand-action hover:underline text-xs font-medium inline-flex items-center gap-1">
                                            <X size={12} /> Cancel
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
